import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';

interface StatCardItem {
  title: string;
  value: number;
  formattedValue: string;
  metricType: 'SUM' | 'COUNT';
}

type MetricType = 'SUM' | 'COUNT';

interface MetricDefinition {
  key: string;
  type: MetricType;
}

interface StatCardColumn {
  items: StatCardItem[];
}

interface StatCardGroup {
  primary: StatCardItem;
  secondary?: StatCardItem;
}

@Component({
  selector: 'app-inbuilt-stat-card',
  templateUrl: './inbuilt-stat-card.component.html',
  styleUrl: './inbuilt-stat-card.component.css',
  standalone: false
})
export class InbuiltStatCardComponent implements OnInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;

  @Output() headingResolved = new EventEmitter<string>();

  private readonly destroy$ = new Subject<void>();
  private selectedFilters: any = {};
  private inputSearchFilter: string = '';
  private lastPayloadSignature = '';

  statGroups: StatCardGroup[] = [];
  loading = false;

  constructor(
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService
  ) {}

  ngOnInit(): void {
    this.selectedFilters = this.config?.selectedFilters || {};
    this.subscribeToFilterEvents();
    this.callStatCardApi();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get gridTemplateColumns(): string {
    const count = Math.min(this.statGroups.length || 1, 3);
    return `repeat(${count}, minmax(0, 1fr))`;
  }

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        this.selectedFilters = event?.selectedFilters || {};
        this.inputSearchFilter = event?.inputSearchFilter || '';
        this.callStatCardApiIfPayloadChanged();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport && eventReport !== myReport) {
      return false;
    }
    if (eventPage && eventPage !== myPage) {
      return false;
    }
    if (eventTab && eventTab !== myTab) {
      return false;
    }

    return true;
  }

  private callStatCardApiIfPayloadChanged(): void {
    const payload = this.buildSelectedFiltersPayload();
    const signature = this.createStableSignature(payload);

    if (signature === this.lastPayloadSignature) {
      return;
    }

    this.callStatCardApi(payload, signature);
  }

  private callStatCardApi(prebuiltSelectedFiltersPayload?: any, prebuiltSignature?: string): void {
    this.loading = true;

    const selectedFiltersPayload = prebuiltSelectedFiltersPayload ?? this.buildSelectedFiltersPayload();
    const signature = prebuiltSignature ?? this.createStableSignature(selectedFiltersPayload);
    this.lastPayloadSignature = signature;

    const payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '{current_decom}',
      elementType: this.elementData?.type || 'card',
      download: false,
      selectedFilters: selectedFiltersPayload,
      inputSearchFilter: this.inputSearchFilter
    };

    this.reportsService.getInbuildTableData(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          const rows = Array.isArray(res?.data) ? res.data : [];
          this.bindStatItems(rows, res?.columnInfo || {});
          this.bindDisplayText(res?.dynamicHeaderTitle || {});
        },
        error: (error: any) => {
          console.error('Stat card API error: ', error);
          this.resetStatCardState();
        }
      });
  }

  private buildSelectedFiltersPayload(): any {
    return this.inbuiltUtilityService.getTablePayload(this.selectedFilters);
  }

  private createStableSignature(value: any): string {
    return JSON.stringify(value || {});
  }

  private bindStatItems(rows: any[], columnInfo: any): void {
    if (!Array.isArray(rows) || rows.length === 0) {
      this.resetStatCardState();
      return;
    }

    const metricDefinitions = this.extractMetricDefinitions(columnInfo);
    if (metricDefinitions.length === 0) {
      this.resetStatCardState();
      return;
    }

    const statItems: StatCardItem[] = metricDefinitions
      .map((definition: MetricDefinition) => {
        const value = rows.reduce((sum: number, row: any) => sum + this.toNumber(row?.[definition.key]), 0);

        return {
          title: definition.key,
          value,
          formattedValue: this.formatMetricValue(value, definition.type),
          metricType: definition.type
        } as StatCardItem;
      })
      .filter((item: StatCardItem) => item.title !== '');

    this.statGroups = this.buildStatGroups(statItems);
  }

  private buildStatGroups(items: StatCardItem[]): StatCardGroup[] {
    const used = new Set<string>();
    const groups: StatCardGroup[] = [];

    for (const item of items) {
      if (used.has(item.title)) {
        continue;
      }

      const normItem = this.normalizeHeaderKey(item.title);
      let matchedOther: StatCardItem | undefined;
      let itemIsPrimary = true;

      for (const other of items) {
        if (other.title === item.title || used.has(other.title)) {
          continue;
        }

        const normOther = this.normalizeHeaderKey(other.title);

        if (normOther.includes(normItem) && normOther !== normItem) {
          // item's key is a substring of other → item is primary, other is secondary
          matchedOther = other;
          itemIsPrimary = true;
          break;
        }

        if (normItem.includes(normOther) && normItem !== normOther) {
          // other's key is a substring of item → other is primary, item is secondary
          matchedOther = other;
          itemIsPrimary = false;
          break;
        }
      }

      used.add(item.title);
      if (matchedOther) {
        used.add(matchedOther.title);
        groups.push(itemIsPrimary
          ? { primary: item, secondary: matchedOther }
          : { primary: matchedOther, secondary: item }
        );
      } else {
        groups.push({ primary: item });
      }
    }

    return groups;
  }

  private extractMetricDefinitions(columnInfo: any): MetricDefinition[] {
    if (!columnInfo || typeof columnInfo !== 'object' || Array.isArray(columnInfo)) {
      return [];
    }

    return Object.keys(columnInfo)
      .map((key: string) => {
        const metricType = String(columnInfo[key] ?? '').trim().toUpperCase();
        if (metricType !== 'SUM' && metricType !== 'COUNT') {
          return null;
        }

        return {
          key,
          type: metricType as MetricType
        };
      })
      .filter((item: MetricDefinition | null): item is MetricDefinition => !!item);
  }

  private formatMetricValue(value: number, metricType: MetricType): string {
    if (metricType === 'SUM') {
      return this.formatCompactCurrency(value);
    }

    return this.formatNumber(value);
  }

  private formatCompactCurrency(value: number): string {
    if (!Number.isFinite(value)) {
      return '$0';
    }

    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);

    if (absValue >= 1_000_000_000) {
      return `${sign}$${(absValue / 1_000_000_000).toFixed(2)}B`;
    }

    if (absValue >= 1_000_000) {
      return `${sign}$${(absValue / 1_000_000).toFixed(2)}M`;
    }

    if (absValue >= 1_000) {
      return `${sign}$${(absValue / 1_000).toFixed(2)}K`;
    }

    return `${sign}$${absValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  }

  private formatNumber(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }

    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const parsed = parseFloat(String(value).replace(/[$,%\s]/g, '').replace(/,/g, '').trim());
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private resetStatCardState(): void {
    this.statGroups = [];
  }

  private normalizeHeaderKey(value: any): string {
    return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private resolveDynamicHeaderTitle(dynamicHeaderTitle: any): string {
    if (!dynamicHeaderTitle || typeof dynamicHeaderTitle !== 'object' || Array.isArray(dynamicHeaderTitle)) {
      return '';
    }

    const elementKey = String(this.elementData?.name ?? '').trim();
    const strippedKey = elementKey.startsWith('{') && elementKey.endsWith('}')
      ? elementKey.slice(1, -1).trim()
      : elementKey;

    const normalizedTarget = this.normalizeHeaderKey(strippedKey || elementKey);

    const matchedKey = Object.keys(dynamicHeaderTitle).find((key: string) =>
      this.normalizeHeaderKey(key) === normalizedTarget
    );

    if (matchedKey) {
      return String(dynamicHeaderTitle[matchedKey] ?? '').trim();
    }

    const firstResolvedValue = Object.values(dynamicHeaderTitle).find((value: any) =>
      typeof value === 'string' && String(value).trim() !== ''
    );

    return String(firstResolvedValue ?? '').trim();
  }

  bindDisplayText(dynamicHeaderTitle: any): void {
    const resolved = this.resolveDynamicHeaderTitle(dynamicHeaderTitle);
    if (!resolved) {
      return;
    }

    this.headingResolved.emit(resolved);
  }
}
